import { useFormContext, useWatch } from 'react-hook-form';

import type { OptionBoxOption, OptionBoxOptionsSource } from '@/features/LeaseDetails';
import { useDynamicCoverageRules } from '@/shared/hooks';

import { calculateLeaseDurationMonths } from '@/shared/utils/leaseDurationMonths.util';

import { useSmbCoverageOptions } from './hooks';
import type { ScreeningResultOption } from './types';
import { buildEmptyOptions, resolveDynamicCoverageOptions } from './utils';
import {
  staticInstitutionalScreeningResultsOptions,
  staticSmbScreeningResultsOptions,
} from './screening-results.options';

// Builds the `option-box` options sources for the screening radios. Each is a
// `kind: 'hook'` source whose `useOptions` reads the relevant lease-info form
// fields and resolves the dynamic (DDS-priced / API-labelled) or static radio
// set — the same logic the legacy `InviteLeaseDetailsDrawerScreeningResults` ran
// inline, now expressed as engine option sources.

const toDate = (iso?: string | null): Date | undefined => (iso ? new Date(iso) : undefined);
const toNumber = (value?: string | null): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(String(value).replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const screeningOptionToOptionBox = (option: ScreeningResultOption): OptionBoxOption => ({
  id: option.id,
  value: option.value ?? '',
  label: option.text,
  subText: option.subText,
  tooltip: option.tooltip,
  disabled: option.disabled,
  disabledTooltip: option.disabledTooltip,
});

// Institutional: dynamic API labels/tooltips merged onto the static results,
// or locked placeholders until an address is chosen.
export const institutionalScreeningOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'hook',
  useOptions: () => {
    const { control } = useFormContext();
    const address = useWatch({ control, name: 'address' });
    const leaseStartDate = useWatch({ control, name: 'leaseStartDate' });
    const leaseEndDate = useWatch({ control, name: 'leaseEndDate' });
    const grossMonthlyRent = useWatch({ control, name: 'grossMonthlyRent' });
    const prepaidRent = useWatch({ control, name: 'prepaidRent' });
    const freeRent = useWatch({ control, name: 'freeRent' });

    const addressSelected = Boolean(address?.monolith_building_id);
    const isLocked = !addressSelected;

    const { data, isLoading, isError } = useDynamicCoverageRules({
      propertyId: address?.property_id,
      leaseStart: toDate(leaseStartDate),
      leaseEnd: toDate(leaseEndDate),
      grossMonthlyRent: toNumber(grossMonthlyRent),
      prepaidRent: toNumber(prepaidRent),
      freeRent: toNumber(freeRent),
      enabled: Boolean(address?.property_id),
    });

    const resolved = isLocked
      ? buildEmptyOptions(staticInstitutionalScreeningResultsOptions.length)
      : resolveDynamicCoverageOptions(staticInstitutionalScreeningResultsOptions, data);

    return {
      options: resolved.map(screeningOptionToOptionBox),
      isLoading: isLoading && addressSelected,
      isError,
    };
  },
});

// SMB legacy (no DDS pricing): the static Low/Medium/Max coverage radios.
export const smbStaticScreeningOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'static',
  options: staticSmbScreeningResultsOptions.map(screeningOptionToOptionBox),
});

// SMB new coverages: DDS-priced "N months / $X max coverage" radios (falls back
// to the static month radios when DDS omits pricing).
export const smbMonthsOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'hook',
  useOptions: () => {
    const { control } = useFormContext();
    const address = useWatch({ control, name: 'address' });
    const leaseStartDate = useWatch({ control, name: 'leaseStartDate' });
    const leaseEndDate = useWatch({ control, name: 'leaseEndDate' });
    const grossMonthlyRent = useWatch({ control, name: 'grossMonthlyRent' });
    const prepaidRent = useWatch({ control, name: 'prepaidRent' });
    const freeRent = useWatch({ control, name: 'freeRent' });
    const leaseGuaranteeMonthsOverride = useWatch({
      control,
      name: 'leaseGuaranteeMonthsOverride',
    });
    const selectedCoverageRuleId = useWatch({ control, name: 'selectedCoverageRuleId' });

    const start = toDate(leaseStartDate);
    const end = toDate(leaseEndDate);

    const { coverageOptions, isLoading, isError } = useSmbCoverageOptions({
      propertyId: address?.property_id,
      leaseStart: start,
      leaseEnd: end,
      grossMonthlyRent: toNumber(grossMonthlyRent),
      prepaidRent: toNumber(prepaidRent),
      freeRent: toNumber(freeRent),
      leaseDurationMonths: calculateLeaseDurationMonths(start, end),
      selectedValue: toNumber(leaseGuaranteeMonthsOverride),
      selectedId: selectedCoverageRuleId,
      enabled: true,
    });

    return {
      options: coverageOptions.map((option) => ({
        id: option.id,
        value: option.value,
        label: option.text,
        subText: option.subText,
        disabled: option.disabled,
      })),
      isLoading,
      isError,
    };
  },
});
